<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useAppStore } from '../store'
import InfoTip from '../components/InfoTip.vue'
import { addDays } from '../lib/tide'
import { levelAt, peakAround } from '@quicktide/shared'
import { useDerivedSeries, useDerivedActuals, bestCurve } from '../lib/series'
import PlaceTabs from '../components/PlaceTabs.vue'
import PlaceTitle from '../components/PlaceTitle.vue'
import { springPhrase } from '../lib/springs'

const store = useAppStore()
const now = ref(Date.now())
let timer: number | undefined
onMounted(() => { timer = window.setInterval(() => { now.value = Date.now() }, 30_000) })
onUnmounted(() => window.clearInterval(timer))

const dayStart = computed(() => store.selectedDay)
const { points, extrema } = useDerivedSeries(() => ({ from: dayStart.value - 86_400_000, to: addDays(dayStart.value, 1) + 86_400_000 }))
const { observed, forecast } = useDerivedActuals()

const shallow = computed(() => store.shallow)
const bridge = computed(() => store.bridge)
const sub = computed(() => store.sublocation)

const kind = computed(() => {
  if (shallow.value) return store.t('kindShallow')
  if (bridge.value) return store.t('kindBridge')
  if (store.settings.sublocationId) return store.t('kindDerived')
  return store.t('kindStation')
})

/** The skipper's own rise or fall, else measurements or forecast where they reach, else astronomy */
const best = computed(() => bestCurve(points.value, observed.value, forecast.value, store.settings.surge))
const level = computed(() => levelAt(best.value, now.value))
const fromActuals = computed(() => !!store.settings.surge || levelAt([...observed.value, ...forecast.value].sort((a, b) => a.time - b.time), now.value) !== null)

const springs = computed(() => springPhrase(now.value, store.station?.springLagDays, store.t, store.tv))

/** "2:15 na HW", the old app's Stand getij, counted from the high water the water actually takes */
const tideState = computed(() => {
  const hw = extrema.value.filter(e => e.type === 'HW').map(e => peakAround(best.value, e.time, 'HW') ?? e)
  if (!hw.length) return null
  const nearest = hw.reduce((a, b) => (Math.abs(b.time - now.value) < Math.abs(a.time - now.value) ? b : a))
  const diff = now.value - nearest.time
  const mins = Math.round(Math.abs(diff) / 60_000)
  const hhmm = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`
  if (mins < 5) return store.t('atHW')
  return `${hhmm} ${diff > 0 ? store.t('afterHW') : store.t('beforeHW')}`
})

/**
 * Water over a shallow, or room under a bridge, right now. Below the seabed there is no water
 * at all rather than a negative amount of it, as the tide screen also says.
 */
const nowFigure = computed(() => {
  if (level.value === null) return null
  if (shallow.value) {
    const depth = (level.value + (store.soundedDepth ?? shallow.value.depth)) / 100
    return { label: store.t('currentDepth'), text: depth <= 0 ? store.t('dry') : `${depth.toFixed(2)} m` }
  }
  if (bridge.value) return { label: store.t('currentHeadroom'), text: `${((bridge.value.height - level.value) / 100).toFixed(2)} m` }
  return null
})

/** "LAT = NAP -134 cm", how the alternative datum sits against NAP */
const datum = computed(() => {
  const st = store.station
  if (!st || st.altRef === undefined) return null
  const sign = st.altRef >= 0 ? '+' : '−'
  return `${st.altRefName ?? 'LAT'} = ${store.napDatumName} ${sign} ${Math.abs(st.altRef)} cm`
})

const shift = computed(() => {
  const own = shallow.value ?? bridge.value
  const s = sub.value
  const parts: string[] = []
  const mins = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n)} min`
  if (own && (own.hwOffsetMinutes || own.lwOffsetMinutes)) {
    parts.push(`${store.t('high')} ${mins(own.hwOffsetMinutes)}`, `${store.t('low')} ${mins(own.lwOffsetMinutes)}`)
  }
  if (s) parts.push(`${s.name}: ${store.t('high')} ${mins(s.hwLater2)}, ${store.t('low')} ${mins(s.lwLater2)}`)
  return parts.length ? parts.join(' · ') : null
})

/** Only shallows carry a warning; every derived kind can carry a note */
const warningText = computed(() => shallow.value?.warning)
const noteText = computed(() => shallow.value?.note ?? bridge.value?.note ?? sub.value?.note)

/** The old files put line breaks in as `//` */
const lines = (text?: string) => (text ? text.split(/\s*\/\/\s*/).filter(Boolean) : [])

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(store.settings.locale, { year: 'numeric', month: 'long', day: 'numeric' })
}
function fmtMetres(cm: number) {
  return `${(cm / 100).toFixed(2)} m`
}
</script>

<template>
  <main class="screen">
    <div class="header">
      <PlaceTitle />
      <PlaceTabs />
    </div>

    <p v-for="(w, i) in lines(warningText)" :key="'w' + i" class="notice warn">{{ w }}</p>

    <!-- the old app's toolbar star: keep the place on screen at hand -->
    <button class="fav" :class="{ on: store.isFavourite }" @click="store.toggleFavourite()">
      <span>{{ store.isFavourite ? '★' : '☆' }}</span>
      {{ store.isFavourite ? store.t('removeFavourite') : store.t('addFavourite') }}
    </button>

    <h2>{{ store.t('thisPlace') }}</h2>
    <div class="setting"><span>{{ store.t('kindLabel') }}</span><span>{{ kind }}</span></div>
    <div v-if="store.station" class="setting"><span>{{ store.t('tideFrom') }}</span><span>{{ store.station.name }}</span></div>
    <div v-if="shift" class="setting"><span>{{ store.t('shiftLabel') }}</span><span class="num">{{ shift }}</span></div>
    <div v-if="datum" class="setting"><span>{{ store.t('referenceLevel') }}</span><span class="num">{{ datum }}</span></div>
    <p v-for="(t, i) in lines(noteText)" :key="'n' + i" class="notice">{{ t }}</p>

    <h2>{{ store.t('rightNow') }}</h2>
    <div v-if="tideState" class="setting"><span>{{ store.t('tideState') }}</span><span class="num">{{ tideState }}</span></div>
    <div class="setting"><span>{{ store.t('springNeap') }}</span><span>{{ springs }}</span></div>
    <div class="setting">
      <span>{{ store.t('level') }}</span>
      <span class="num" :class="{ measured: fromActuals }">{{ level === null ? '–' : store.displayLevel(level).toFixed(2) }} m {{ store.refLevelName }}</span>
    </div>
    <div v-if="nowFigure" class="setting">
      <span>{{ nowFigure.label }}</span>
      <span class="num" :class="{ measured: fromActuals }">{{ nowFigure.text }}</span>
    </div>

    <template v-if="shallow">
      <h2>{{ store.t('sounding') }} <InfoTip :title="store.t('sounding')" :texts="[store.t('soundingNote')]" /></h2>
      <div v-if="store.ownDepth" class="setting">
        <span>{{ store.t('myDepth') }}</span>
        <span class="stepper">
          <span class="num value">{{ fmtMetres(store.ownDepth.depth) }} {{ store.t('belowDatum') }} {{ store.napDatumName }}</span>
          <button class="link" @click="store.clearOwnDepth()">{{ store.t('clearMyDepth') }}</button>
        </span>
      </div>
      <div v-if="store.ownDepth" class="setting"><span>{{ store.t('myDepthTaken') }}</span><span class="num">{{ fmtDate(store.ownDepth.at) }}</span></div>
      <div class="setting"><span>{{ store.t('soundedDepth') }}</span><span class="num" :class="{ muted: store.ownDepth }">{{ fmtMetres(shallow.depth) }} {{ store.t('belowDatum') }} {{ store.napDatumName }}</span></div>
      <div v-if="shallow.surveyed" class="setting"><span>{{ store.t('surveyedOn') }}</span><span class="num">{{ fmtDate(shallow.surveyed) }}</span></div>
      <div v-if="shallow.source" class="setting"><span>{{ store.t('sourceLabel') }}</span><span>{{ shallow.source }}</span></div>
    </template>

    <template v-if="bridge">
      <h2>{{ store.t('theBridge') }}</h2>
      <div class="setting"><span>{{ store.t('clearanceHeight') }}</span><span class="num">{{ fmtMetres(bridge.height) }} {{ store.t('aboveDatum') }} {{ store.napDatumName }}</span></div>
      <div v-if="bridge.surveyed" class="setting"><span>{{ store.t('surveyedOn') }}</span><span class="num">{{ fmtDate(bridge.surveyed) }}</span></div>
      <div v-if="bridge.source" class="setting"><span>{{ store.t('sourceLabel') }}</span><span>{{ bridge.source }}</span></div>
    </template>

  </main>
</template>
